import type { JSX } from 'react';

import clsx from 'clsx';

import { testIds } from '@/components/constants/testIds';
import { composeAlignmentClasses } from '@/components/functions/composeAlignmentClasses';
import { composeFlexDirectionClasses } from '@/components/functions/composeFlexDirectionClasses';
import { composeFlexWrapClasses } from '@/components/functions/composeFlexWrapClasses';
import { composeGapSizeClasses } from '@/components/functions/composeGapSizeClasses';
import { composeHorizontalGapSizeClasses } from '@/components/functions/composeHorizontalGapSizeClasses';
import { composeJustifyClasses } from '@/components/functions/composeJustifyClasses';
import { composeVerticalGapSizeClasses } from '@/components/functions/composeVerticalGapSizeClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Alignment } from '@/components/types/Alignment';
import type { FlexDirection } from '@/components/types/FlexDirection';
import type { FlexWrap } from '@/components/types/FlexWrap';
import type { Justify } from '@/components/types/Justify';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';

type StackProps = {
    children: React.ReactNode;
    direction?: Adaptive<FlexDirection>;
    wrap?: Adaptive<FlexWrap>;
    justify?: Adaptive<Justify>;
    alignment?: Adaptive<Alignment>;
    width?: Adaptive<Width>;
    gap?: Adaptive<number>;
    hGap?: Adaptive<number>;
    vGap?: Adaptive<number>;
    className?: string;
    testId?: string;
};

export default function Stack({
    children,
    direction,
    wrap,
    justify = componentsConfig.stack.justify,
    alignment = componentsConfig.stack.alignment,
    width = componentsConfig.stack.width,
    gap,
    hGap,
    vGap,
    className,
    testId = testIds.stack.base,
}: StackProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div
            data-testid={testId}
            className={clsx(
                'flex',
                composeJustifyClasses(justify),
                composeAlignmentClasses(alignment),
                composeWidthClasses(width),
                direction && composeFlexDirectionClasses(direction),
                wrap && composeFlexWrapClasses(wrap),
                gap && composeGapSizeClasses(gap),
                hGap && composeHorizontalGapSizeClasses(hGap),
                vGap && composeVerticalGapSizeClasses(vGap),
                className
            )}
        >
            {children}
        </div>
    );
}
